import styled from "styled-components";
import { transparentize } from "polished";

import { StopPauseButtons as _StopPauseButtons } from "_/components/stop-pause-buttons";
import { MachineCameraMedia as _MachineCameraMedia } from "_/components/machine-camera-media";
import { MaterialColorBox } from "_/components/materials/styled";
import { Button } from "_/components/button/styled";
import { numericFontStyles } from "_/style";

export const LayerControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  height: 100%;
  width: max-content;

  padding: 1rem;

  border-radius: 5px;

  background-color: ${(p) => p.theme.background.secondary};
`;

export const PointCloudControlContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  width: 100%;
  height: max-content;

  padding: 1rem;
  border-radius: 5px;

  background-color: ${(p) => p.theme.background.secondary};
`;

export const LayerSliderContainer = styled.div`
  display: flex;
  flex-grow: 1;
  justify-content: center;
`;

export const ControlLabel = styled.span`
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.muted};
  font-weight: 600;

  & input[type="checkbox"]:not(:checked):not(:focus):not(:active) {
    border-color: ${(p) => p.theme.text.muted};
  }
`;

export const SensorRangeControl = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
`;

export const RangeLabelRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
  font-weight: 600;
`;

export const RangeValue = styled.span`
  ${numericFontStyles}
  font-weight: 400;
  white-space: nowrap;
  cursor: text;
  border-bottom: 1px dotted
    ${(p) => transparentize(0.38, p.theme.border.contrast)};

  &:focus-visible {
    border-radius: 2px;
    outline: 1px solid ${(p) => p.theme.text.muted};
    outline-offset: 2px;
  }
`;

export const RangeValueGroup = styled.span`
  display: inline-flex;
  align-items: baseline;
  gap: 0.3rem;
`;

export const RangeValueInput = styled.input`
  ${numericFontStyles}
  width: 7ch;
  padding: 0;
  border: 0;
  border-bottom: 1px solid ${(p) => p.theme.text.muted};
  outline: 0;
  color: inherit;
  background: transparent;
  font: inherit;
  font-weight: 400;
  text-align: right;

  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    appearance: none;
    margin: 0;
  }
`;

export const RangePlot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FullRangeOverview = styled.div`
  position: relative;
  height: 9px;
  flex: none;
  cursor: pointer;
  touch-action: none;
  margin-bottom: 3px;

  &::before {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: ${(p) => p.theme.background.quaternary};
  }
`;

export const FullRangeViewport = styled.div<{ $left: number; $right: number }>`
  position: absolute;
  bottom: 0;
  height: 5px;
  left: ${(p) => p.$left}%;
  right: ${(p) => p.$right}%;
  background: ${(p) => p.theme.text.muted};
  cursor: pointer;

  &:active {
    cursor: grabbing;
  }

  &::before,
  &::after {
    content: "";
    position: absolute;
    bottom: -4.5px;
    width: 4px;
    height: 14px;
    border-radius: 2px;
    background: ${(p) => p.theme.text.default};
    cursor: pointer;
  }

  &::before {
    left: -1.5px;
  }

  &::after {
    right: -1.5px;
  }
`;

export const HistogramFrame = styled.div`
  width: 100%;
  height: 4rem;
  flex: none;
  overflow: hidden;
  border-radius: 3px;
  background: ${(p) => p.theme.background.tertiary};
`;

export const ColorRangeRail = styled.div`
  position: relative;
  height: 14px;
  flex: none;
  color: ${(p) => p.theme.text.default};
`;

export const HistogramSvg = styled.svg`
  display: block;
  width: 100%;
  height: 100%;
  color: ${(p) => p.theme.text.muted};

  rect {
    fill: currentColor;
    transition: opacity 80ms linear;
  }
`;

export const MachineCameraMediaContainer = styled.div`
  display: flex;
  padding: 0.625rem;
  background-color: ${(p) => p.theme.background.secondary};
  border-radius: 5px;
  flex-direction: column;
  gap: 0.5rem;
`;

export const GeneratingTimelapseMessage = styled.div`
  text-align: center;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
`;

export const MachineCameraMediaInnerWrapper = styled.div`
  aspect-ratio: 16 / 9;
`;

export const MachineCameraMedia = styled(_MachineCameraMedia)`
  width: 100%;
  height: 100%;
`;

export const RightSideContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

export const ProjectInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${(p) => p.theme.text.default};
`;

export const ProjectInfoItemColumns = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`;

export const ProjectInfoLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 0.375rem;
  color: ${(p) => p.theme.text.muted};
  font-weight: 600;

  & ${Button} {
    padding: 0;
    &:enabled {
      outline: 0;
    }
  }
`;

export const StopPauseButtons = styled(_StopPauseButtons)`
  justify-content: flex-end;
`;

export const ProjectInfoValue = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: space-between;

  & ${MaterialColorBox} {
    margin-right: 0.5rem;
  }
`;

export const ProgressWrapper = styled.div`
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const SpinnerSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  color: ${(p) => p.theme.text.muted};
  font-size: 0.875rem;
  text-align: center;
`;
